import { StateObservable, combineEpics, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  groupBy,
  map,
  mergeMap,
  startWith,
  throttleTime,
} from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import { ReduxAction } from '../types';
import { apiObservable } from '@support/api';
import { AppState } from '@reducers/rootReducer';
import Occasion from '@support/Occasion';
import {
  selectContactTimeline,
  selectContactTimelineWindowSummary,
  selectFilteredContactTimeline,
} from '@reducers/data/timeline';
import { selectContact } from './contacts';

/* Epic actions */
const timelineActions = requestActions('CONTACT_TIMELINE', ['fetch']);
const timelineAvailableFiltersActions = requestActions('CONTACT_TIMELINE_AVAILABLE_FILTERS', [
  'fetch',
]);
const timelineWindowActions = requestActions('CONTACT_TIMELINE_WINDOW', ['fetch']);
const timelineWindowSummaryActions = requestActions('CONTACT_TIMELINE_WINDOW_SUMMARY', ['fetch']);
const filteredTimelineActions = requestActions('FILTERED_CONTACT_TIMELINE', ['fetch']);

/* Component actions */
export const fetchContactTimeline = timelineActions['fetch'];
export const fetchContactTimelineAvailableFilters = timelineAvailableFiltersActions['fetch'];
export const fetchContactTimelineWindow = timelineWindowActions['fetch'];
export const fetchFilteredContactTimeline = filteredTimelineActions['fetch'];

/* Epic helper functions */
const getContactID = (payload) => String(payload.contact?.id || payload.contactID);

const fetchCollapsedTimelineObservable =
  ({ dropExistingWindowSummary }: { dropExistingWindowSummary: boolean }) =>
  ({ payload }: ReduxAction) =>
    apiObservable(
      'GET',
      `/contacts/${getContactID(payload)}/timeline/`,
      { query: { collapse_notes: true } },
      {
        apiVersion: 'v2',
      },
    ).pipe(
      map((response) => {
        return timelineActions['fetchSucceeded']({
          ...response,
          contactID: getContactID(payload),
          dropExistingWindowSummary,
        });
      }),
      startWith(timelineActions['fetchRequested'](payload)),
      catchError((err) => of(timelineActions['fetchFailed'](err))),
    );

/* Epics */
const fetchTimelineEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('SELECT_CONTACT'),
    filter(({ payload }) => !!payload?.contact || !!payload?.contactID),
    groupBy(({ payload }) => getContactID(payload)),
    mergeMap((fetchesByID) =>
      fetchesByID.pipe(
        // For a given contact, only fetch the condensed timeline at most once per minute
        throttleTime(1000 * 60 * 1),
        mergeMap(fetchCollapsedTimelineObservable({ dropExistingWindowSummary: false })),
      ),
    ),
  );

// This handles race condition between note creation and timeline fetch for manually added contacts
const fetchTimelineAfterContactNoteCreationEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('CREATE_NOTE_SUCCEEDED'),
    filter(({ payload }) => {
      const contactID = payload.response?.contact;

      if (!contactID) {
        return false;
      }

      const contact = selectContact(String(contactID))(state$.value);
      const twoMinutesInDays = 2 / (24 * 60);
      const contactCreatedInLastTwoMinutes = contact.created.isWithin(twoMinutesInDays);
      return contactCreatedInLastTwoMinutes;
    }),
    mergeMap(({ payload }) => {
      const contactID = String(payload.response.contact);
      return fetchCollapsedTimelineObservable({ dropExistingWindowSummary: false })({
        payload: { contactID },
      } as ReduxAction);
    }),
  );

// there are two functional difference between fetching after a merge and fetching after a select:
// 1. after a merge, we don't throttle the timeline fetch.
// 2. after a merge, we need to re-fetch the initial summary so that subsequent window fetches work properly, hence the dropExistingWindowSummary flag
const fetchTimelineAfterMergeEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('MERGE_CONTACT_BATCH_SUCCEEDED'),
    filter(({ payload }) => !!payload?.contact || !!payload?.contactID),
    mergeMap(fetchCollapsedTimelineObservable({ dropExistingWindowSummary: true })),
  );

const fetchTimelineAvailableFiltersEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_CONTACT_TIMELINE_AVAILABLE_FILTERS'),
    mergeMap(({ payload }) =>
      apiObservable('GET', `/contacts/${getContactID(payload)}/timeline/filters/`, undefined, {
        apiVersion: 'v2',
      }).pipe(
        map((response) => {
          return timelineAvailableFiltersActions['fetchSucceeded']({
            ...response,
            contactID: getContactID(payload),
          });
        }),
        startWith(timelineAvailableFiltersActions['fetchRequested'](payload)),
        catchError((err) => of(timelineAvailableFiltersActions['fetchFailed'](err))),
      ),
    ),
  );

const fetchTimelineWindowEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_CONTACT_TIMELINE_WINDOW'),
    mergeMap(({ payload }) => {
      const contactID = getContactID(payload);
      const tl = selectContactTimeline(contactID)(state$.value);
      const windowSummary = selectContactTimelineWindowSummary(contactID)(state$.value);

      // If this is the first window getting fetched, we haven't determined a proper start date yet (subsequently, this will
      // be the last item date in the last window fetched). Use the first timeline item date as the start of the window to
      // ensure that we don't miss any items.
      const start = windowSummary?.start || tl?.items?.first()?.date;

      const windowDays = payload.window || -14;
      let end = new Occasion(start).addDays(windowDays).toISOString();
      // if there are fewer than 50 items in the window, fetch everything at once
      if (windowSummary && (windowSummary.total || 0) < 50) {
        end = tl?.items?.last()?.date;
      }

      return apiObservable(
        'GET',
        `/contacts/${contactID}/timeline/`,
        {
          query: {
            start,
            end,
          },
        },
        {
          apiVersion: 'v2',
        },
      ).pipe(
        map((response) => {
          // Check to see if there are new items in the response that we don't already have in state
          // if no new items, repeat this with a larger window...
          const hasNewItems = response.response.results.some((item) => !tl?.items.has(item?.id));

          if (hasNewItems) {
            return timelineWindowActions['fetchSucceeded']({
              ...response,
              contactID,
              window,
              end,
            });
          } else {
            return timelineWindowActions['fetch']({
              ...payload,
              window: windowDays * 2,
            });
          }
        }),
        startWith(timelineActions['fetchRequested'](payload)),
        catchError((err) => of(timelineActions['fetchFailed'](err))),
      );
    }),
  );

const fetchTimelineWindowSummaryEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_CONTACT_TIMELINE_SUCCEEDED', 'FETCH_CONTACT_TIMELINE_WINDOW_SUCCEEDED'),
    mergeMap(({ payload }) => {
      const contactID = getContactID(payload);
      const windowSummary = selectContactTimelineWindowSummary(contactID)(state$.value);
      const tl = selectContactTimeline(contactID)(state$.value);
      let start = payload.end || windowSummary?.start;
      const end = tl?.items?.last()?.date;

      // if the window summary has been dropped, we need to re-fetch the initial summary (i.e. after a MERGE).
      // Use the start and end from the timeline data in state for this contact
      if (!windowSummary) {
        start = tl?.items?.first()?.date;
      }

      return apiObservable(
        'GET',
        `/contacts/${contactID}/timeline/summary/`,
        { query: { start, end } },
        {
          apiVersion: 'v2',
        },
      ).pipe(
        map((response) => {
          return timelineWindowSummaryActions['fetchSucceeded']({
            ...response,
            contactID,
            start,
            end,
          });
        }),
        startWith(timelineWindowSummaryActions['fetchRequested'](payload)),
        catchError((err) => of(timelineWindowSummaryActions['fetchFailed'](err))),
      );
    }),
  );

const fetchFilteredTimelineEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_FILTERED_CONTACT_TIMELINE'),
    mergeMap(({ payload }) => {
      const contactID = getContactID(payload);
      const loadedFilteredTimeline = selectFilteredContactTimeline(
        contactID,
        payload.filterType,
      )(state$.value);
      let body: { query: { date: string } } = undefined;

      if (loadedFilteredTimeline) {
        body = { query: { date: loadedFilteredTimeline.lastDate } };
      }

      return apiObservable('GET', `/contacts/${contactID}/timeline/${payload.filterType}/`, body, {
        apiVersion: 'v2',
      }).pipe(
        map((response) => {
          return filteredTimelineActions['fetchSucceeded']({
            ...response,
            contactID,
            filterType: payload.filterType,
          });
        }),
        startWith(filteredTimelineActions['fetchRequested'](payload)),
        catchError((err) => of(filteredTimelineActions['fetchFailed'](err))),
      );
    }),
  );

export const timelineEpic = combineEpics(
  fetchTimelineEpic,
  fetchTimelineAfterContactNoteCreationEpic,
  fetchTimelineAfterMergeEpic,
  fetchTimelineAvailableFiltersEpic,
  fetchTimelineWindowEpic,
  fetchTimelineWindowSummaryEpic,
  fetchFilteredTimelineEpic,
);
